export const JOB_CARD_IMAGES_BUCKET = 'job-card-images';
export const JOB_CARD_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const JOB_CARD_VIDEO_MAX_BYTES = 50 * 1024 * 1024;
export const JOB_CARD_IMAGE_MAX_COUNT = 8;

export type JobCardImageKind = 'comment' | 'survey_notes';

export const JOB_CARD_IMAGE_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
] as const;

export const JOB_CARD_VIDEO_MIMES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/3gpp',
  'video/x-m4v',
] as const;

const VIDEO_NAME = /\.(mp4|mov|webm|m4v|3gp)$/i;
const IMAGE_NAME = /\.(jpe?g|png|webp|gif|heic|heif)$/i;

export function isJobCardVideo(file: Pick<File, 'type' | 'name'> | { mime_type?: string | null; file_name?: string | null }): boolean {
  const type = String('type' in file ? file.type : file.mime_type || '').toLowerCase();
  const name = String('name' in file ? file.name : file.file_name || '');
  return type.startsWith('video/') || VIDEO_NAME.test(name);
}

export function jobCardImageExtension(fileName: string, mime?: string | null): string {
  const type = String(mime || '').toLowerCase();
  if (type.includes('quicktime')) return 'mov';
  if (type.includes('webm')) return 'webm';
  if (type.includes('3gpp')) return '3gp';
  if (type.includes('m4v')) return 'm4v';
  if (type.includes('mp4')) return 'mp4';
  if (type.includes('png')) return 'png';
  if (type.includes('webp')) return 'webp';
  if (type.includes('gif')) return 'gif';
  if (type.includes('heic') || type.includes('heif')) return 'heic';
  if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
  const fromName = String(fileName || '').match(/\.([a-z0-9]+)$/i);
  if (fromName) return fromName[1].toLowerCase();
  return type.startsWith('video/') ? 'mp4' : 'jpg';
}

export function sanitizeJobCardFileName(name: string): string {
  return String(name || '').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 180) || 'media';
}

export function jobCardImageStoragePath(input: {
  projectId: string;
  jobCardId: string;
  kind: JobCardImageKind;
  imageId: string;
  fileName: string;
  mimeType?: string | null;
}): string {
  const ext = jobCardImageExtension(input.fileName, input.mimeType);
  return `${input.projectId}/${input.jobCardId}/${input.kind}/${input.imageId}.${ext}`;
}

export function capJobCardImages(currentCount: number, incoming: number): string | null {
  if (currentCount + incoming > JOB_CARD_IMAGE_MAX_COUNT) {
    return `You can attach up to ${JOB_CARD_IMAGE_MAX_COUNT} files here.`;
  }
  return null;
}

export function isAllowedJobCardImage(file: Pick<File, 'type' | 'size' | 'name'>): string | null {
  const type = String(file.type || '').toLowerCase();
  const video = isJobCardVideo(file);
  const image = type.startsWith('image/') || IMAGE_NAME.test(file.name);
  if (!video && !image) return 'Choose a photo or video.';
  if (video && file.size > JOB_CARD_VIDEO_MAX_BYTES) return 'Each video must be 50 MB or smaller.';
  if (!video && file.size > JOB_CARD_IMAGE_MAX_BYTES) return 'Each photo must be 10 MB or smaller.';
  return null;
}
